import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { getHocSinhByLop, getDiemSoHocSinh } from "../../services/hocSinhService";
import { getDiemDanhByNgay } from "../../services/diemDanhService";
import { getNamHocActive, getActiveHocKy } from "../../services/namHocService";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../config/firebase";
import { getCache, setCache } from "../../services/cacheService";

const GVCNDashboard = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const lopId = user?.maLopCN || "";

  const [hocSinhList, setHocSinhList] = useState([]);
  const [diemDanhData, setDiemDanhData] = useState([]);
  const [stats, setStats] = useState({ tyLeCC: 0, topVang: [], topGioi: [], topYeu: [] });
  const [loading, setLoading] = useState(true);
  const [namHoc, setNamHoc] = useState("2025-2026");
  const [hocKy, setHocKy] = useState("HK1");
  const today = new Date();
  const isSunday = today.getDay() === 0;

  const getSundayDate = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d; };
  const sundayDate = getSundayDate();
  const ngayCN = sundayDate.toISOString().split('T')[0];
  const ngayCNFormatted = sundayDate.toLocaleDateString('vi-VN', { day: 'numeric', month: 'long' });

  useEffect(() => {
    const init = async () => {
      try { const nh = await getNamHocActive(); if (nh) setNamHoc(nh.tenNamHoc || nh.id); } catch(e) {}
      try { const hk = await getActiveHocKy(); if (hk) setHocKy(hk.hocKy); } catch(e) {}
    };
    init();
  }, []);

  useEffect(() => {
    if (!lopId) return;
    const fetchData = async () => {
      const cacheKey = `gvcn_dashboard_v3_${lopId}_${namHoc}_${hocKy}`;
      const cached = getCache(cacheKey);
      if (cached) {
        setHocSinhList(cached.hs);
        setDiemDanhData(cached.dd);
        setStats(cached.stats);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [hsSnap, ddSnap] = await Promise.all([
          getDocs(query(collection(db, "hocSinh"), where("lopHienTaiId", "==", lopId))),
          getDocs(query(collection(db, "diemDanh"), where("lopId", "==", lopId))),
        ]);

        const hsList = hsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const ddList = ddSnap.docs.map(d => d.data());

        // Chuyên cần
        const filtered = ddList.filter(d => {
          if (!d.ngayHoc) return false;
          const m = new Date(d.ngayHoc).getMonth() + 1;
          const y = new Date(d.ngayHoc).getFullYear();
          const [namBD] = namHoc.split('-');
          const namKT = namBD ? parseInt(namBD) + 1 : 2026;
          if (!((y === parseInt(namBD) && m >= 8) || (y === namKT && m <= 5))) return false;
          return hocKy === 'HK1' ? (m >= 8 && m <= 12) : (m >= 1 && m <= 5);
        });
        const tongBuoi = [...new Set(filtered.map(d => d.ngayHoc))].length;
        const coMat = filtered.filter(d => d.trangThai === 'coMat' || d.trangThai === 'diTre').length;
        const tyLeCC = hsList.length > 0 && tongBuoi > 0 ? Math.round((coMat / (hsList.length * tongBuoi)) * 100) : 0;

        const vangMap = {};
        filtered.filter(d => d.trangThai === 'vangKhongPhep').forEach(d => { vangMap[d.hocSinhId] = (vangMap[d.hocSinhId] || 0) + 1; });
        const topVang = Object.entries(vangMap).sort((a,b) => b[1]-a[1]).slice(0,3).map(([id, count]) => {
          const hs = hsList.find(h => h.id === id);
          return { ...hs, vangKP: count };
        }).filter(Boolean);

        // Điểm số - load top
        const dsMap = {};
        for (const hs of hsList) {
          const dsSnap = await getDocs(collection(db, "hocSinh", hs.id, "diemSo"));
          dsMap[hs.id] = dsSnap.docs.map(d => d.data()).filter(d => d.namHoc === namHoc && d.hocKy === hocKy);
        }

        const hsWithDiem = hsList.map(hs => {
          const ds = dsMap[hs.id] || [];
          const m = ds.filter(d => d.loaiDiem === 'mieng').map(d => d.diem);
          const m15 = ds.filter(d => d.loaiDiem === '15phut').map(d => d.diem);
          const m1 = ds.filter(d => d.loaiDiem === '1tiet').map(d => d.diem);
          const hk = ds.filter(d => d.loaiDiem === 'hocKy').map(d => d.diem);
          if (m.length>0 && m15.length>0 && m1.length>0 && hk.length>0) {
            return { ...hs, diemTB: parseFloat(((m.reduce((a,b)=>a+b,0)/m.length + m15[0] + m1[0]*2 + hk[0]*3)/7).toFixed(1)) };
          }
          return { ...hs, diemTB: null };
        });

        const topGioi = hsWithDiem.filter(h => h.diemTB !== null).sort((a,b) => b.diemTB - a.diemTB).slice(0,3);
        const topYeu = hsWithDiem.filter(h => h.diemTB !== null).sort((a,b) => a.diemTB - b.diemTB).slice(0,3);

        const newStats = { tyLeCC, tongBuoi, topVang, topGioi, topYeu };

        const ddHomNay = await getDiemDanhByNgay(lopId, ngayCN);
        const result = { hs: hsList, dd: ddHomNay, stats: newStats };
        setHocSinhList(hsList);
        setDiemDanhData(ddHomNay);
        setStats(newStats);
        setCache(cacheKey, result);
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [lopId, namHoc, hocKy, ngayCN]);

  const getRoleName = () => {
    const roles = user?.vaiTro || [];
    if (roles.includes('ADMIN')) return 'Admin';
    if (roles.includes('TRUONG_NGANH')) return 'Trưởng ngành';
    if (roles.includes('GVCN')) return 'GVCN';
    if (roles.includes('GV_PHU')) return 'GV Phụ';
    return '';
  };

  const siSo = hocSinhList.length;
  const daDiemDanh = diemDanhData.filter(d => d.trangThai === 'coMat' || d.trangThai === 'diTre').length;
  const vangHomNay = diemDanhData.filter(d => d.trangThai === 'vangPhep' || d.trangThai === 'vangKhongPhep').length;
  const chuaDiemDanh = siSo - diemDanhData.length;
  const daDiemDanhXong = diemDanhData.length > 0 && chuaDiemDanh === 0;
  const formatDate = () => today.toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const tenLop = user?.tenLop || user?.maLopCN || 'Chưa có lớp';

  if (loading) return <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>;

  return (
    <div className="space-y-4 md:space-y-5">
      {/* WELCOME */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Xin chào, {getRoleName()} {user?.tenThanh ? `${user?.tenThanh} ` : ''}{user?.hoTen || '...'}</h1>
          <p className="text-body-md text-on-surface-variant mt-1 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-primary-container text-on-primary-container rounded-full text-label-caps">{tenLop}</span>
            <span className="text-caption">{namHoc} • {hocKy}</span>
          </p>
        </div>
        <div className="text-caption text-on-surface-variant bg-surface-container-low px-3 py-1.5 rounded-full flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">calendar_today</span>{formatDate()}
        </div>
      </section>

      {/* BANNER CN */}
      {isSunday && !daDiemDanhXong && (
        <div className="bg-[#FFF8E1] border border-[#FFB300]/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 animate-pulse">
          <span className="material-symbols-outlined text-[#FF8F00] text-3xl">campaign</span>
          <div className="flex-1"><p className="text-body-lg font-bold">Cần điểm danh hôm nay!</p><p className="text-body-md">Còn <strong className="text-error">{siSo} HS</strong> chưa điểm danh</p></div>
          <button onClick={() => navigate('/diem-danh')} className="bg-primary text-on-primary px-5 py-3 rounded-xl font-bold">Điểm danh ngay →</button>
        </div>
      )}
      {((isSunday && daDiemDanhXong) || !isSunday) && diemDanhData.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <span className="material-symbols-outlined text-green-600 text-3xl">check_circle</span>
          <div className="flex-1"><p className="text-body-lg text-green-800 font-bold">{isSunday ? 'Đã điểm danh xong!' : `Điểm danh CN ${ngayCNFormatted}`}</p><p className="text-body-md text-green-700">{daDiemDanh} có mặt · {vangHomNay} vắng</p></div>
          <button onClick={() => navigate('/diem-danh')} className="text-body-md text-green-700 font-semibold hover:underline">Xem →</button>
        </div>
      )}

      {/* 2 STATS */}
      <div className="grid grid-cols-2 gap-2 md:gap-3">
        {[
          { icon: 'groups', label: 'Sĩ số', value: siSo, sub: `${stats.tongBuoi||0} buổi HK`, color: 'text-primary', bg: 'bg-primary-container', path: '/quan-ly-hoc-sinh' },
          { icon: 'fact_check', label: 'Chuyên cần', value: `${stats.tyLeCC}%`, sub: 'cả học kỳ', color: stats.tyLeCC>=90?'text-green-600':stats.tyLeCC>=70?'text-yellow-600':'text-error', bg: stats.tyLeCC>=90?'bg-green-100':stats.tyLeCC>=70?'bg-yellow-100':'bg-error-container', path: '/diem-danh' },
        ].map((c,i) => (
          <button key={i} onClick={() => navigate(c.path)} className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant flex flex-col justify-between hover:shadow-md transition-all active:scale-[0.98] text-left">
            <div className="flex justify-between items-start mb-2"><span className="text-body-md text-on-surface-variant">{c.label}</span><div className={`p-1.5 rounded-full ${c.bg}`}><span className={`material-symbols-outlined ${c.color} text-xl`}>{c.icon}</span></div></div>
            <div className={`text-[32px] font-bold ${c.color}`}>{c.value}</div>
            <span className="text-caption text-on-surface-variant/60 mt-1">{c.sub}</span>
          </button>
        ))}
      </div>

      {/* CHUYÊN CẦN + TOP ĐIỂM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
        {/* Chuyên cần */}
        <div className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-body-lg font-bold text-primary">📋 Chuyên cần</h3>
            <button onClick={() => navigate('/diem-danh')} className="text-caption text-primary font-semibold">Xem →</button>
          </div>
          <div className="w-full h-2.5 bg-surface-container rounded-full overflow-hidden mb-2">
            <div className={`h-full rounded-full ${stats.tyLeCC>=90?'bg-green-500':stats.tyLeCC>=70?'bg-yellow-500':'bg-error'}`} style={{width:`${stats.tyLeCC}%`}}/>
          </div>
          {stats.topVang.length > 0 ? (
            <div className="space-y-1">
              <p className="text-caption text-error font-bold">⚠️ Vắng KP nhiều:</p>
              {stats.topVang.map(hs => (
                <button key={hs.id} onClick={() => navigate(`/hoc-sinh/${hs.id}`)} className="w-full text-left px-3 py-1.5 bg-error-container/20 text-error rounded-lg text-caption">
                  {hs.tenThanh ? `Th. ${hs.tenThanh} - ` : ''}{hs.hoTen} • {hs.vangKP} KP
                </button>
              ))}
            </div>
          ) : <p className="text-caption text-green-600">✅ Không HS vắng KP nhiều</p>}
        </div>

        {/* Top điểm */}
        <div className="bg-surface rounded-xl p-4 shadow-sm border border-outline-variant">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-body-lg font-bold text-primary">📊 Điểm số</h3>
            <button onClick={() => navigate('/nhap-diem')} className="text-caption text-primary font-semibold">Xem →</button>
          </div>
          {stats.topGioi.length > 0 && (
            <div className="mb-3">
              <p className="text-caption text-green-600 font-bold mb-1">🏆 Cao nhất:</p>
              {stats.topGioi.map(hs => (
                <button key={hs.id} onClick={() => navigate(`/hoc-sinh/${hs.id}`)} className="w-full text-left px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-caption mb-1 flex justify-between">
                  <span>{hs.tenThanh ? `Th. ${hs.tenThanh} - ` : ''}{hs.hoTen}</span>
                  <span className="font-bold">{hs.diemTB}</span>
                </button>
              ))}
            </div>
          )}
          {stats.topYeu.length > 0 && (
            <div>
              <p className="text-caption text-error font-bold mb-1">🔴 Thấp nhất:</p>
              {stats.topYeu.map(hs => (
                <button key={hs.id} onClick={() => navigate(`/hoc-sinh/${hs.id}`)} className="w-full text-left px-3 py-1.5 bg-error-container/20 text-error rounded-lg text-caption mb-1 flex justify-between">
                  <span>{hs.tenThanh ? `Th. ${hs.tenThanh} - ` : ''}{hs.hoTen}</span>
                  <span className="font-bold">{hs.diemTB}</span>
                </button>
              ))}
            </div>
          )}
          {stats.topGioi.length === 0 && stats.topYeu.length === 0 && (
            <p className="text-caption text-on-surface-variant">Chưa có đủ điểm để xếp hạng</p>
          )}
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <section>
        <h2 className="text-section-title text-primary mb-3 font-bold">Thao tác nhanh</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { icon: 'fact_check', label: 'Điểm danh', path: '/diem-danh', primary: true },
            { icon: 'edit_document', label: 'Nhập điểm', path: '/nhap-diem' },
            { icon: 'folder_shared', label: 'Hồ sơ HS', path: '/quan-ly-hoc-sinh' },
            { icon: 'grading', label: 'Đánh giá', path: '/danh-gia-cuoi-ky' },
          ].map((a,i) => (
            <button key={i} onClick={() => navigate(a.path)} className={`rounded-xl p-4 flex flex-col items-center justify-center gap-2 active:scale-95 transition-all ${a.primary ? 'bg-primary text-on-primary shadow-sm hover:bg-primary/90' : 'bg-surface text-primary border border-primary/20 shadow-sm hover:bg-surface-variant'}`}>
              <span className="material-symbols-outlined text-3xl">{a.icon}</span>
              <span className="text-body-lg font-semibold text-center">{a.label}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default GVCNDashboard;